import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Users as UsersIcon, Loader2, Pencil, KeyRound, AlertTriangle, ShieldOff, UserX, UserCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import OnlineDot, { statusFromLastSeen } from "@/components/OnlineDot";
import UserFormDialog from "@/components/users/UserFormDialog";
import { ROLE_OPTIONS, roleLabel } from "@/lib/roles";
import { plural } from "@/lib/plural";

const ALL = "__all__";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [roleFilter, setRoleFilter] = useState(ALL);
  const [mfaOn, setMfaOn] = useState(false);
  useEffect(() => { api.get("/auth/config").then(({ data }) => setMfaOn(!!data.mfa_enabled)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const [u, o] = await Promise.all([api.get("/users"), api.get("/organizations")]);
      setUsers(u.data); setOrgs(o.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 60 * 1000); return () => clearInterval(id); }, [load]);

  const filtered = useMemo(
    () => (roleFilter === ALL ? users : users.filter((u) => (u.roles || []).includes(roleFilter))),
    [users, roleFilter],
  );
  const notNominative = users.filter((u) => !u.is_nominative && u.active);
  const clientiNoOrg = users.filter((u) => (u.roles || []).includes("cliente") && !u.organization_id && u.active);

  async function save(form) {
    try {
      const payload = {
        first_name: form.first_name.trim(), last_name: form.last_name.trim(), email: form.email.trim(),
        roles: form.roles,
        organization_id: form.roles.includes("cliente") ? form.organization_id || null : null,
        partner_org_ids: form.roles.includes("partner") ? form.partner_org_ids : [],
      };
      if (form.password) payload.password = form.password;
      if (editing) await api.patch(`/users/${editing.id}`, payload);
      else await api.post("/users", payload);
      toast.success(editing ? "Utente aggiornato" : "Utente creato");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  async function action(fn, okMsg, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { const r = await fn(); if (okMsg) toast.success(okMsg); load(); return r; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  async function resetPassword(u) {
    const r = await action(() => api.post(`/users/${u.id}/reset-password`), "Password reimpostata",
      `Generare una nuova password temporanea per ${u.email}?\n\nL'utente sarà costretto a cambiarla al prossimo accesso.`);
    if (r) window.prompt(r.data.email_sent ? `Email inviata a ${u.email}. Password temporanea:` : "Password temporanea (comunicala all'utente):", r.data.temporary_password);
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2">Amministrazione</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Utenti</h1>
          <p className="text-slate-500 mt-1">Account nominativi, ruoli e separazione delle funzioni.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-11 w-56 rounded-lg" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tutti i ruoli</SelectItem>
              {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-user-button" className="h-11 px-5 rounded-lg bg-viva-500 hover:bg-viva-600 text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Utente
          </Button>
        </div>
      </div>

      <Warnings notNominative={notNominative} clientiNoOrg={clientiNoOrg} />

      <Card className="rounded-2xl border-slate-100 shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500"><UsersIcon className="w-10 h-10 mx-auto mb-3 text-slate-300" />Nessun utente.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-3" style={{ width: 28 }}><span className="sr-only">Stato</span></th>
                  <th className="px-3 py-3">Nome</th>
                  <th className="hidden md:table-cell px-3 py-3">Email</th>
                  <th className="px-3 py-3">Ruoli</th>
                  <th className="hidden lg:table-cell px-3 py-3">Azienda collegata</th>
                  <th className="hidden xl:table-cell px-3 py-3">Ultimo accesso</th>
                  <th className="hidden xl:table-cell px-3 py-3">Tentativi falliti</th>
                  {mfaOn && <th className="hidden sm:table-cell px-3 py-3">2FA</th>}
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => (
                  <UserRow key={u.id} u={u} mfaOn={mfaOn}
                    onEdit={() => { setEditing(u); setOpen(true); }}
                    onReset={() => resetPassword(u)}
                    onReset2fa={() => action(() => api.post(`/users/${u.id}/reset-2fa`), "2FA azzerata: l'utente la riconfigurerà al prossimo accesso", `Azzerare l'autenticazione a due fattori di ${u.email}?`)}
                    onToggleActive={() => u.active
                      ? action(() => api.post(`/users/${u.id}/deactivate`), "Utente disattivato", `Disattivare ${u.email}? L'account non potrà più accedere (i dati restano conservati).`)
                      : action(() => api.post(`/users/${u.id}/reactivate`), "Utente riattivato")}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <UserFormDialog open={open} editing={editing} orgs={orgs} onClose={() => { setOpen(false); setEditing(null); }} onSave={save} />
    </div>
  );
}

function Warnings({ notNominative, clientiNoOrg }) {
  if (!notNominative.length && !clientiNoOrg.length) return null;
  return (
    <div className="space-y-2 mb-5">
      {notNominative.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex gap-2" data-testid="warning-not-nominative">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b>{plural(notNominative.length, "account non nominativo", "account non nominativi")} (nome o cognome mancante o generico).</b> Gli account generici non sono ammessi: completa i dati di {notNominative.map((u) => u.email).join(", ")}.</span>
        </div>
      )}
      {clientiNoOrg.length > 0 && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 flex gap-2" data-testid="warning-cliente-no-org">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span><b>{plural(clientiNoOrg.length, "utente Cliente non collegato", "utenti Cliente non collegati")} ad alcuna azienda:</b> {clientiNoOrg.map((u) => u.email).join(", ")}.</span>
        </div>
      )}
    </div>
  );
}

function UserRow({ u, mfaOn, onEdit, onReset, onReset2fa, onToggleActive }) {
  const s = statusFromLastSeen(u.last_seen_at, u.created_at);
  const isCliente = (u.roles || []).includes("cliente");
  const company = isCliente ? u.organization_name : (u.partner_org_names || []).filter(Boolean).join(", ");
  return (
    <tr data-testid={`user-row-${u.id}`} className={u.active ? "" : "opacity-50 bg-slate-50"}>
      <td className="px-3 py-3 align-top"><OnlineDot lastSeenAt={u.last_seen_at} createdAt={u.created_at} testid={`user-online-${u.id}`} /></td>
      <td className="px-3 py-3 font-medium text-slate-900 align-top">
        <div className="flex items-center gap-2 flex-wrap">
          <span>{u.name || "—"}</span>
          {!u.is_nominative && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800" data-testid={`user-not-nominative-${u.id}`}>Non nominativo</span>}
          {!u.active && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-700" data-testid={`user-inactive-${u.id}`}>Disattivato</span>}
        </div>
        <div className="text-[11px] font-normal text-slate-400">{s.label}</div>
        <div className="md:hidden mt-1 text-xs text-slate-500 break-all">{u.email}</div>
      </td>
      <td className="hidden md:table-cell px-3 py-3 text-slate-600 align-top">{u.email}</td>
      <td className="px-3 py-3 align-top">
        <div className="flex flex-wrap gap-1" data-testid={`user-roles-${u.id}`}>
          {(u.roles || []).map((r) => <span key={r} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">{roleLabel(r)}</span>)}
        </div>
      </td>
      <td className="hidden lg:table-cell px-3 py-3 align-top text-slate-600" data-testid={`user-company-${u.id}`}>
        {company || (isCliente ? <span className="text-red-600 text-xs font-semibold">Nessuna azienda</span> : "—")}
      </td>
      <td className="hidden xl:table-cell px-3 py-3 align-top text-xs text-slate-600" data-testid={`user-last-login-${u.id}`}>
        {u.last_login_at ? new Date(u.last_login_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
      </td>
      <td className="hidden xl:table-cell px-3 py-3 align-top text-xs" data-testid={`user-failed-logins-${u.id}`}>
        <span className={u.failed_login_count ? "text-red-600 font-semibold" : "text-slate-400"}>{u.failed_login_count || 0}</span>
      </td>
      {mfaOn && (
        <td className="hidden sm:table-cell px-3 py-3 align-top">
          {u.totp_enabled ? <ShieldCheck className="w-4 h-4 text-emerald-600" data-testid={`user-2fa-on-${u.id}`} /> : <ShieldOff className="w-4 h-4 text-slate-300" data-testid={`user-2fa-off-${u.id}`} />}
        </td>
      )}
      <td className="px-3 py-3 text-right align-top">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`user-edit-${u.id}`} title="Modifica utente"><Pencil className="w-4 h-4 text-viva-600" /></Button>
          <Button variant="ghost" size="icon" onClick={onReset} data-testid={`user-reset-${u.id}`} title="Reimposta password"><KeyRound className="w-4 h-4 text-amber-600" /></Button>
          {mfaOn && u.totp_enabled && <Button variant="ghost" size="icon" onClick={onReset2fa} data-testid={`user-reset-2fa-${u.id}`} title="Azzera autenticazione a due fattori"><ShieldOff className="w-4 h-4 text-slate-600" /></Button>}
          <Button variant="ghost" size="icon" onClick={onToggleActive} data-testid={`user-toggle-active-${u.id}`} title={u.active ? "Disattiva utente" : "Riattiva utente"}>
            {u.active ? <UserX className="w-4 h-4 text-red-500" /> : <UserCheck className="w-4 h-4 text-emerald-600" />}
          </Button>
        </div>
      </td>
    </tr>
  );
}
